/**
 * Tiny module-level pub/sub so non-React modules (notably `services/api.ts`) can publish toasts that the React `ToastProvider` displays.
 *
 * The api layer cannot use the `useToast` hook because it runs outside the React tree. Hoisting the toast hook into a context-aware singleton would also work but adds complexity; this bus is intentionally minimal.
 */

import type { ToastType } from "../components/Toast";

export interface ToastEvent {
  type: ToastType;
  message: string;
  duration?: number;
}

type Listener = (event: ToastEvent) => void;

const listeners = new Set<Listener>();

export const subscribeToasts = (listener: Listener): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const publishToast = (event: ToastEvent): void => {
  // Fan out to every subscriber; ToastProvider is the only expected consumer in production, but tests may attach their own.
  listeners.forEach((listener) => {
    try {
      listener(event);
    } catch {
      // A misbehaving subscriber must not break others.
    }
  });
};
